import React, {useEffect, useRef, useState} from 'react'
import {
    Box,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import {SaveInventoryItem, SaveInventoryRequestItem} from '../utils'
import {webEvents} from '../../common/WebEvents'
import excludedSound from '../../assets/sounds/excluded.mp3'
import {ExpectedSerials} from '../transferUtils'
import {ExcludedDetailsDialog, ItemStatus, SerialInput} from '../../common/ui'
import {CachedProduct} from '../productCache'

interface SerialStepProps {
    products: CachedProduct[]
    serialItems: SaveInventoryItem[]
    saveRequestItem: (requestItem: SaveInventoryRequestItem) => void
    expectedSerials: ExpectedSerials[]
}

const ReceiveSerials = ({
                            products,
                            serialItems,
                            saveRequestItem,
                            expectedSerials
                        }: SerialStepProps) => {
    const [currentSerial, setCurrentSerial] = useState<string>('')
    const [open, setOpen] = useState(false)
    const [selectedItem, setSelectedItem] = useState<SaveInventoryItem | null>(null)
    const audioPlayer = useRef<HTMLAudioElement | null>(null)
    const [savedQty, setSavedQty] = useState<number | null>(null)
    const [savedExcludedQty, setSavedExcludedQty] = useState<number | null>(0)
    const [pendingQty, setPendingQty] = useState<number | null>(null)
    const [unexpectedQty, setUnexpectedQty] = useState<number | null>(null)
    let inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        if (window.sessionStorage.getItem('blocking') === 'true' && inputRef.current) {
            inputRef.current.blur()
        } else if (inputRef.current) {
            inputRef.current.focus()
        }
    }, [inputRef, window.sessionStorage.getItem('blocking')])

    const getModelNumber = (sku: string) => {
        const product = products.find(p => p.sku === sku)
        return product ? product.model : 'Unknown Model'
    }

    const getLocation = (sku: string) => {
        const product = products.find(p => p.sku === sku)
        return product && product.bin ? product.bin : 'N/A'
    }

    const handleClickOpen = (item: SaveInventoryItem) => {
        setSelectedItem(item)
        setOpen(true)
    }

    const handleClose = () => {
        setOpen(false)
        setSelectedItem(null)
    }

    const handleCopy = (str: string) => {
        navigator.clipboard.writeText(str)
            .then(() => {
                alert('Copied to clipboard!')
            })
            .catch(err => {
                console.error('Failed to copy: ', err)
            })
    }

    const reportSerialItems = [...serialItems].reverse()

    const handleAddSerial = () => {
        // check that length is between 1 and 128
        if (currentSerial && currentSerial.length > 0 && currentSerial.length < 128) {
            saveRequestItem({
                op: 'add',
                sku: '',
                quantity: 1,
                serial: currentSerial,
                bin: null,
                enteredAt: new Date().toISOString()
            })

            setCurrentSerial('')
        } else {
            webEvents.warning('serial not valid', {serial: currentSerial})
        }
    }

    const handleRemoveSerial = (serial: string | null, enteredAt: string) => {
        saveRequestItem({
            op: 'delete',
            sku: '',
            quantity: 1,
            serial,
            bin: null,
            enteredAt
        })
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' || event.key === 'Tab') {
            event.preventDefault()
            handleAddSerial()
        }
    }

    useEffect(() => {
        let serialQty = 0
        let serialExcludedQty = 0
        let serialUnexpectedQty = 0
        let serialPendingQty = 0
        for (let item of reportSerialItems) {
            if (item.progress) {
                serialPendingQty += item.quantity
                if (item.progress === 'q-delete' || item.progress === 'deleting' && item.excluded) {
                    serialExcludedQty += item.quantity
                } else if (item.progress === 'q-delete' || item.progress === 'deleting') {
                    serialQty += item.quantity
                }
            } else if (item.excluded) {
                serialExcludedQty += item.quantity
            } else if (!findExpectedSerialItem(item)) {
                serialUnexpectedQty += item.quantity
            } else {
                serialQty += item.quantity

            }
        }
        setSavedQty(serialQty)
        setSavedExcludedQty(serialExcludedQty)
        setPendingQty(serialPendingQty)
        setUnexpectedQty(serialUnexpectedQty)
    }, [reportSerialItems])

    const findExpectedSerialItem = (serialItem: SaveInventoryItem) => {
        if (expectedSerials.length > 0) {
            return expectedSerials.some(item => item.serial === serialItem.serial)
        }
        return false
    }

    const receievedQuantityExceeded = (serialItem: SaveInventoryItem) => {
        if (expectedSerials) {
            const found = expectedSerials.find(item => item.serial === serialItem.serial)
            if (found) {
                return found.quantity < serialItem.quantity
            }
            return true
        }
    }

    return (
        <>
            <Box
                flex={1}
                p={2}
                border={2}
                borderColor="grey.400"
                borderRadius={2}
                position="relative">
                <Typography variant="h5">Scan/Enter Serials</Typography>
                <Typography variant="subtitle1" color="textSecondary">
                    <span style={{fontWeight: 700, color: '#2e7d32'}}>Saved: </span>
                    {savedQty === 1 ? `${savedQty} serial` : `${savedQty} serials`},{' '}
                    <span style={{color: '#ff7f00'}}> {`${unexpectedQty} unexpected`},{' '} </span>
                    <span style={{color: 'red'}}>{savedExcludedQty} excluded </span> <span
                    style={{fontWeight: 700}}>Pending: </span>
                    {pendingQty} quantity
                </Typography>
                <audio ref={audioPlayer} src={excludedSound} />

                <SerialInput
                    currentSerial={currentSerial}
                    setCurrentSerial={setCurrentSerial}
                    handleKeyDown={handleKeyDown}
                    inputRef={inputRef}
                />

                <TableContainer component={Paper} className="my-3">
                    <Table size="small">
                        <TableHead>
                            <TableRow
                                style={{
                                    borderBottom:
                                        reportSerialItems.length > 0 &&
                                        reportSerialItems[0].excluded
                                            ? 'none'
                                            : '2px solid #ddd'
                                }}>
                                <TableCell sx={{display: 'none'}}></TableCell>
                                <TableCell>Item</TableCell>
                                <TableCell>Serial</TableCell>
                                <TableCell>Model</TableCell>
                                <TableCell>Location</TableCell>
                                <TableCell>Expected</TableCell>
                                <TableCell>Received</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Remove</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {reportSerialItems.length > 0 ? (reportSerialItems.map(item => {
                                    const excluded = item.progress === 'q-delete' || item.progress === 'deleting'
                                        ? '' : item.excluded
                                    const unexpectedQty = receievedQuantityExceeded(item)
                                    return (
                                        <TableRow
                                            key={item.serial}
                                            sx={{
                                                border: item.excluded ? '2px solid red' : 'none',
                                                color: item.excluded ? 'red' : 'inherit'
                                            }}
                                        >
                                            <TableCell
                                                sx={{display: 'none'}}></TableCell>
                                            <TableCell>
                                                {unexpectedQty ? item.excluded ?
                                                        <span style={{color: 'red'}}>{item.sku}</span> :
                                                        <span style={{color: '#ff7f00'}}>{item.sku}</span>
                                                    : item.sku}
                                            </TableCell>
                                            <TableCell>{unexpectedQty ? item.excluded ?
                                                    <span style={{color: 'red'}}>{item.serial}</span> :
                                                    <span style={{color: '#ff7f00'}}>{item.serial}</span>
                                                : item.serial}
                                            </TableCell>
                                            <TableCell>{unexpectedQty ? item.excluded ?
                                                    <span style={{color: 'red'}}>{getModelNumber(item.sku)}</span> :
                                                    <span style={{color: '#ff7f00'}}>{getModelNumber(item.sku)}</span>
                                                : getModelNumber(item.sku)}
                                            </TableCell>
                                            <TableCell>{unexpectedQty ? item.excluded ?
                                                    <span style={{color: 'red'}}>{getLocation(item.sku)}</span> :
                                                    <span style={{color: '#ff7f00'}}>{getLocation(item.sku)}</span>
                                                : getLocation(item.sku)}
                                            </TableCell>
                                            <TableCell>
                                                {findExpectedSerialItem(item) ? 1 : item.excluded ?
                                                    <span style={{color: 'red'}}>excluded</span> :
                                                    <span style={{color: '#ff7f00'}}>unexpected</span>}
                                            </TableCell>
                                            <TableCell>
                                                {unexpectedQty ? item.excluded ?
                                                        <span style={{color: 'red'}}>{item.quantity}</span> :
                                                        <span style={{color: '#ff7f00'}}>{item.quantity}</span>
                                                    : item.quantity}
                                            </TableCell>
                                            <TableCell>
                                                {excluded ? (
                                                    <Tooltip title={<h1 style={{
                                                        color: 'text.primary',
                                                        fontSize: 15,
                                                        fontWeight: 'medium'
                                                    }}>{excluded.startsWith('not-found:') ? 'not-found' : excluded}</h1>}>
                                                        <Box
                                                            fontSize="small"
                                                            sx={{
                                                                cursor: 'pointer',
                                                                color: 'red'
                                                            }}
                                                            onClick={() => handleClickOpen(item)}>
                                                            {excluded.split(':')[0]}
                                                        </Box>
                                                    </Tooltip>
                                                ) : (
                                                    <ItemStatus progress={item.progress} />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <IconButton
                                                    sx={{cursor: 'pointer'}}
                                                    size="small"
                                                    onClick={e => {
                                                        e.stopPropagation()
                                                        handleRemoveSerial(
                                                            item.serial, item.enteredAt
                                                        )
                                                    }}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    )
                                })
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        No saved serial items
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {selectedItem && (
                    <ExcludedDetailsDialog
                        open={open}
                        selectedItem={selectedItem}
                        onClose={handleClose}
                        onCopy={handleCopy}
                    />
                )}
            </Box>
        </>
    )
}

export default ReceiveSerials
